import { AnimatePresence, motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, useFetcher } from 'react-router'
import { useSearchDrawer } from '#app/contexts/search-drawer-context.tsx'
import { useDebounce, getNoteImgSrc } from '#app/utils/misc.tsx'
import { titleToSlug } from '#app/utils/slug.ts'
import { Icon } from './ui/icon.tsx'

type Note = {
	id: string
	title: string
	content: string
	updatedAt: Date
	username: string
	ownerName: string | null
	images: Array<{
		id: string
		objectKey: string
		altText: string | null
	}>
}

export function SearchDrawer() {
	const { isOpen, setIsOpen } = useSearchDrawer()
	const [searchTerm, setSearchTerm] = useState('')
	const [mounted, setMounted] = useState(false)
	const fetcher = useFetcher<{ notes: Note[] }>()
	const inputRef = useRef<HTMLInputElement>(null)
	const { t } = useTranslation()

	useEffect(() => setMounted(true), [])

	const debouncedSearch = useDebounce((term: string) => {
		if (term.trim()) {
			void fetcher.load(`/api/search/notes?q=${encodeURIComponent(term)}`)
		}
	}, 300)

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setSearchTerm(value)
		debouncedSearch(value)
	}

	const handleResultClick = () => {
		setIsOpen(false)
		setSearchTerm('')
	}

	const open = useCallback(() => setIsOpen(true), [setIsOpen])
	const close = useCallback(() => {
		setIsOpen(false)
		setSearchTerm('')
	}, [setIsOpen])

	useEffect(() => {
		function onKeyDown(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault()
				if (isOpen) {
					close()
				} else {
					open()
				}
			}
			if (e.key === 'Escape' && isOpen) {
				close()
			}
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [isOpen, open, close])

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
			const timer = setTimeout(() => inputRef.current?.focus(), 100)
			return () => {
				clearTimeout(timer)
				document.body.style.overflow = ''
			}
		} else {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	const notes = fetcher.data?.notes || []
	const isSearching = fetcher.state === 'loading'
	const hasResults = notes.length > 0
	const hasSearched = searchTerm.trim().length > 0

	return (
		<>
			{/* Trigger button */}
			<button
				onClick={open}
				className="flex h-10 w-10 items-center justify-center transition-opacity hover:opacity-60"
				style={{
					color: 'var(--ink)',
					background: 'none',
					border: 'none',
					cursor: 'pointer',
				}}
				aria-label={t('searchSculptures')}
			>
				<Icon name="magnifying-glass" className="h-[18px] w-[18px]" />
			</button>

			{/* Full-screen overlay — portalled to body to escape header stacking context */}
			{mounted &&
				createPortal(
					<AnimatePresence>
						{isOpen && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								className="fixed inset-0 z-[100]"
								style={{
									padding: 'var(--frame-w)',
								}}
							>
								{/* Backdrop */}
								<motion.div
									className="absolute inset-0"
									style={{
										background: 'var(--paper)',
									}}
									onClick={close}
								/>

								{/* Content */}
								<motion.div
									initial={{ opacity: 0, y: -8 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -8 }}
									transition={{
										duration: 0.25,
										ease: [0.22, 1, 0.36, 1],
									}}
									className="relative flex h-full flex-col"
								>
							{/* Header */}
							<div
								className="flex items-center justify-between px-[8vw] py-5"
								style={{ borderBottom: '1px solid var(--line)' }}
							>
								<span className="eyebrow">{t('search')}</span>
								<button
									onClick={close}
									className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[var(--paper-2)]"
									style={{
										background: 'transparent',
										border: 0,
										cursor: 'pointer',
									}}
									aria-label="Close"
								>
									<Icon name="cross-1" className="h-5 w-5" />
								</button>
							</div>

							{/* Search input */}
							<div className="px-[8vw] pt-10 pb-8">
								<div className="relative">
									<input
										ref={inputRef}
										type="search"
										value={searchTerm}
										onChange={handleInputChange}
										placeholder={t('searchPlaceholder')}
										className="w-full border-0 border-b bg-transparent pb-4 text-[clamp(24px,4vw,40px)] font-medium tracking-tight outline-none"
										style={{
											fontFamily: 'var(--font-heading)',
											color: 'var(--ink)',
											borderColor: searchTerm
												? 'var(--ink)'
												: 'var(--line)',
											letterSpacing: '-0.02em',
											transition:
												'border-color var(--t-fast) var(--ease)',
										}}
										aria-label={t('searchSculptures')}
									/>
									<div className="absolute right-0 bottom-5">
										{isSearching ? (
											<Icon
												name="update"
												className="h-5 w-5 animate-spin"
												style={{
													color: 'var(--muted-gallery)',
												}}
											/>
										) : (
											<Icon
												name="magnifying-glass"
												className="h-5 w-5"
												style={{
													color: 'var(--muted-gallery)',
												}}
											/>
										)}
									</div>
								</div>
								<div className="mt-3 flex items-center justify-between">
									<p
										className="text-[13px]"
										style={{ color: 'var(--muted-gallery)' }}
									>
										{t('searchHint')}
									</p>
									<kbd
										className="hidden items-center gap-1 text-[11px] sm:flex"
										style={{ color: 'var(--muted-2)' }}
									>
										<span
											className="rounded px-1.5 py-0.5"
											style={{
												background: 'var(--paper-2)',
												border: '1px solid var(--line)',
												fontFamily: 'var(--font-sans)',
											}}
										>
											⌘K
										</span>
									</kbd>
								</div>
							</div>

							{/* Results area */}
							<div
								className="flex-1 overflow-y-auto px-[8vw] pb-12"
								role="region"
								aria-live="polite"
								aria-label={t('searchSculptures')}
							>
								{/* No results */}
								{hasSearched && !isSearching && !hasResults && (
									<motion.div
										initial={{ opacity: 0, y: 8 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.3 }}
										className="flex flex-col items-center pt-16"
									>
										<p
											className="text-center text-lg"
											style={{ color: 'var(--ink-soft)' }}
										>
											{t('searchNoResults', { query: searchTerm })}
										</p>
										<p
											className="mt-2 text-center text-[13px]"
											style={{ color: 'var(--muted-gallery)' }}
										>
											{t('searchTryAgain')}
										</p>
									</motion.div>
								)}

								{/* Results grid */}
								{hasResults && (
									<div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-4">
										{notes.map((note, i) => {
											const slug = titleToSlug(note.title)
											return (
												<motion.div
													key={note.id}
													initial={{ opacity: 0, y: 12 }}
													animate={{ opacity: 1, y: 0 }}
													transition={{
														duration: 0.35,
														delay: i * 0.04,
														ease: [0.22, 1, 0.36, 1],
													}}
												>
													<Link
														to={`/beelden/${slug}`}
														onClick={handleResultClick}
														className="group block"
													>
														<div className="relative aspect-[4/5] overflow-hidden">
															{note.images.length > 0 &&
															note.images[0]?.objectKey ? (
																<Img
																	alt={
																		note.images[0]?.altText ??
																		note.title
																	}
																	src={getNoteImgSrc(
																		note.images[0].objectKey,
																	)}
																	className="h-full w-full object-cover transition-transform duration-[600ms]"
																	style={{
																		transitionTimingFunction:
																			'var(--ease)',
																	}}
																	width={400}
																	height={500}
																/>
															) : (
																<div
																	className="flex h-full w-full items-center justify-center"
																	style={{
																		background:
																			'var(--paper-2)',
																	}}
																>
																	<Icon
																		name="camera"
																		className="h-8 w-8"
																		style={{
																			color: 'var(--muted-2)',
																		}}
																	/>
																</div>
															)}
															<div
																className="absolute inset-0 opacity-0 transition-opacity duration-[360ms] group-hover:opacity-100"
																style={{
																	background:
																		'linear-gradient(to top, rgba(14,20,32,0.25), transparent 50%)',
																}}
															/>
														</div>
														<h3
															className="mt-3 text-[15px] font-medium tracking-tight transition-colors duration-[180ms] group-hover:text-[var(--accent-gallery)]"
															style={{
																color: 'var(--ink)',
																fontFamily:
																	'var(--font-sans)',
																letterSpacing: '-0.01em',
															}}
														>
															{note.title}
														</h3>
													</Link>
												</motion.div>
											)
										})}
									</div>
								)}

								{/* Empty state */}
								{!hasSearched && (
									<motion.div
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{ duration: 0.4, delay: 0.15 }}
										className="flex flex-col items-center pt-16"
									>
										<p
											className="text-center text-lg"
											style={{
												color: 'var(--ink-soft)',
												fontFamily: 'var(--font-heading)',
												fontStyle: 'italic',
											}}
										>
											{t('searchPrompt')}
										</p>
									</motion.div>
								)}
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>,
				document.body,
			)}
		</>
	)
}
